import { useEffect, useState } from "react";
import ExpandableText from "./ExpandableText";
import { ArrowLeft, Image, Pencil, Search, Star, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chat as ChatType, FavoriteMessage } from "../../types";
import { getImageUser, getImageUserNotFound } from "../../global-components/src/utils/auth";
import { useAuth } from "../../global-components/src";
import { BUCKET_PACOCA_URL, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { api } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { GipFavoritesPanel, GipMediaPanel, GipSearchPanel } from "./GroupInfoPanel";
import { useChatMediaSearch } from "./useChatMediaSearch";
import "./group-info-panel.css";
import "./style.css";

interface ChatMenuProps {
    chatSelected: ChatType | null;
    show: boolean;
    onClose: () => void;
    onNicknameChange?: (nickname: string | null) => void;
    onScrollToMessage?: (id: number) => void;
    initialTab?: "nickname" | "media" | "search" | "favorites" | null;
}

const ChatMenu: React.FC<ChatMenuProps> = ({ chatSelected, show, onClose, onNicknameChange, onScrollToMessage, initialTab }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { token } = useAuth();
    const [rendered, setRendered] = useState(show);
    const [entering, setEntering] = useState(false);
    const [nickname, setNickname] = useState("");
    const [savingNickname, setSavingNickname] = useState(false);
    const [nicknameError, setNicknameError] = useState(false);
    const [showNickname, setShowNickname] = useState(false);
    const [showFavorites, setShowFavorites] = useState(false);
    const [favorites, setFavorites] = useState<FavoriteMessage[]>([]);
    const [loadingFavorites, setLoadingFavorites] = useState(false);
    const [favoritesCursor, setFavoritesCursor] = useState<number | null>(null);
    const [favoritesHasMore, setFavoritesHasMore] = useState(false);
    const media = useChatMediaSearch(chatSelected?.chat_id);

    useEffect(() => {
        setNickname(chatSelected?.nickname ?? "");
        setNicknameError(false);
        setShowNickname(false);
        setShowFavorites(false);
        setFavorites([]);
        setFavoritesCursor(null);
    }, [chatSelected?.chat_id, chatSelected?.nickname, show]);

    useEffect(() => {
        media.setShowMedia(false);
        media.setShowMsgSearch(false);
    }, [chatSelected?.chat_id, show]);

    // Open at the requested tab when show becomes true
    useEffect(() => {
        if (!show || !initialTab) return;
        if (initialTab === "nickname") {
            setShowNickname(true);
        } else if (initialTab === "media") {
            media.openMedia();
        } else if (initialTab === "search") {
            media.openMsgSearch();
        } else if (initialTab === "favorites") {
            setShowFavorites(true);
            loadFavorites(null);
        }
    }, [show, initialTab]);

    const loadFavorites = async (cursor?: number | null) => {
        if (loadingFavorites) return;
        setLoadingFavorites(true);
        try {
            const params = new URLSearchParams({ limit: "20" });
            if (cursor) params.set("cursor", String(cursor));
            const res = await api(token).get(`${PACOCA_API_URL}/chats/messages/favorites?${params}`);
            const data: FavoriteMessage[] = res.data.favorites ?? [];
            // Filter to only this chat
            const filtered = data.filter(f => f.chat_id === chatSelected?.chat_id);
            setFavorites(prev => cursor ? [...prev, ...filtered] : filtered);
            setFavoritesHasMore(res.data.has_more ?? false);
            setFavoritesCursor(res.data.next_cursor ?? null);
        } catch {
            // silent
        } finally {
            setLoadingFavorites(false);
        }
    };

    const saveNickname = async () => {
        if (!chatSelected?.user_id || savingNickname) return;
        setSavingNickname(true);
        setNicknameError(false);
        try {
            const response = await api(token).put(`${PACOCA_API_URL}/chats/nickname/${chatSelected.user_id}`, { nickname: nickname.trim() });
            onNicknameChange?.(response.data.nickname ?? null);
        } catch {
            setNicknameError(true);
        } finally {
            setSavingNickname(false);
        }
    };

    useEffect(() => {
        if (show) {
            setRendered(true);
            const raf = requestAnimationFrame(() => setEntering(true));
            return () => cancelAnimationFrame(raf);
        } else {
            setEntering(false);
            const t = setTimeout(() => setRendered(false), 220);
            return () => clearTimeout(t);
        }
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, onClose]);

    const subTitle: string | null =
        media.showMedia ? t("chat.group.mediaLinksTitle")
        : media.showMsgSearch ? t("chat.group.searchMessages")
        : showNickname ? t("chat.nickname")
        : showFavorites ? t("chat.favorites")
        : null;
    const closeSubPanel = () => {
        if (media.showMedia) media.setShowMedia(false);
        else if (media.showMsgSearch) media.setShowMsgSearch(false);
        else if (showNickname) setShowNickname(false);
        else if (showFavorites) setShowFavorites(false);
    };

    if (!rendered) return null;

    return (
        <>
            <div className={`gip-backdrop ${entering ? "gip-in" : ""}`} onClick={onClose} />

            <div className={`gip-panel ${entering ? "gip-in" : ""}`} role="dialog" aria-label="Dados do usuário">
                <div className="gip-header">
                    <div className="gip-header-left">
                        {subTitle && (
                            <button type="button" className="gip-back-btn" onClick={closeSubPanel} aria-label={t("generics.back")}>
                                <ArrowLeft size={20} />
                            </button>
                        )}
                        <h5 className="gip-header-title">{subTitle ?? "Chats"}</h5>
                    </div>
                    {!subTitle && (
                        <button type="button" className="gip-close-btn" onClick={onClose} aria-label="Fechar">
                            <X size={18} />
                        </button>
                    )}
                </div>

                <div className="gip-body">
                    {chatSelected && media.showMedia ? (
                        <GipMediaPanel
                            t={t}
                            mediaTab={media.mediaTab}
                            mediaItems={media.mediaItems}
                            mediaCursor={media.mediaCursor}
                            mediaHasMore={media.mediaHasMore}
                            loadingMedia={media.loadingMedia}
                            onSwitchTab={media.switchMediaTab}
                            onLoadMore={() => media.loadMedia(media.mediaTab, media.mediaCursor)}
                            onScrollToMessage={(id) => { media.setShowMedia(false); onClose(); onScrollToMessage?.(id); }}
                            parsePaths={media.parsePaths}
                            parseTypes={media.parseTypes}
                            extractUrl={media.extractUrl}
                            formatDate={media.formatDate}
                            BUCKET_PACOCA_URL={BUCKET_PACOCA_URL}
                        />
                    ) : chatSelected && media.showMsgSearch ? (
                        <GipSearchPanel
                            t={t}
                            query={media.msgSearchQuery}
                            results={media.msgResults}
                            loading={media.loadingMsgSearch}
                            onQueryChange={(v) => { media.setMsgSearchQuery(v); media.debouncedSetMsgSearch(v); }}
                            onClickResult={(id) => { media.setShowMsgSearch(false); onClose(); onScrollToMessage?.(id); }}
                            highlightText={media.highlightText}
                            formatDate={media.formatDate}
                        />
                    ) : chatSelected && showFavorites ? (
                        <GipFavoritesPanel
                            t={t}
                            favorites={favorites}
                            loading={loadingFavorites}
                            hasMore={favoritesHasMore}
                            onLoadMore={() => loadFavorites(favoritesCursor)}
                            onClickResult={(id) => { setShowFavorites(false); onClose(); onScrollToMessage?.(id); }}
                            formatDate={media.formatDate}
                        />
                    ) : chatSelected && showNickname ? (
                        <div className="gip-all-members-panel">
                            <div className="gip-section">
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input
                                        id="chat-nickname-input"
                                        className="form-control"
                                        type="text"
                                        maxLength={50}
                                        value={nickname}
                                        placeholder={chatSelected.original_name ?? t("chat.nicknamePlaceholder")}
                                        onChange={(e) => setNickname(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") saveNickname(); }}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="foc-btn foc-btn-primary"
                                        disabled={savingNickname}
                                        onClick={saveNickname}
                                    >
                                        {t("chat.nicknameSave")}
                                    </button>
                                </div>
                                {nicknameError && <p style={{ color: "#dc3545", fontSize: 13, marginTop: 6 }}>{t("chat.nicknameError")}</p>}
                            </div>
                        </div>
                    ) : chatSelected && (
                        <>
                            {/* Avatar */}
                            <div className="gip-avatar-wrap">
                                <div className="gip-avatar-circle">
                                    <img
                                        className="gip-avatar-img"
                                        src={getImageUser(chatSelected.img_account)}
                                        onError={(e) => { e.currentTarget.src = getImageUserNotFound(chatSelected.name); }}
                                        alt="Foto de perfil"
                                    />
                                </div>
                            </div>

                            {/* Name */}
                            <div className="gip-name-row">
                                <h5 className="gip-name">{chatSelected.name}</h5>
                            </div>
                            <p className="gip-subtitle">@{chatSelected.user_name}</p>

                            {chatSelected.biography?.trim() && (
                                <div className="gip-bio">
                                    <ExpandableText
                                        text={chatSelected.biography}
                                        showMoreLabel={t("chat.group.showMore")}
                                        showLessLabel={t("chat.group.showLess")}
                                    />
                                </div>
                            )}

                            {/* Entry rows: Apelido (private), Mídia/Links/Docs & Pesquisar */}
                            <div className="gip-section gip-entry-section">
                                <div className="gip-entry-row" onClick={() => setShowNickname(true)}>
                                    <span className="gip-entry-icon"><Pencil size={17} /></span>
                                    <span className="gip-entry-label">{t("chat.nickname")}</span>
                                    <span className="gip-entry-chevron">›</span>
                                </div>
                                <div className="gip-entry-row" onClick={media.openMedia}>
                                    <span className="gip-entry-icon"><Image size={17} /></span>
                                    <span className="gip-entry-label">{t("chat.group.mediaLinksTitle")}</span>
                                    <span className="gip-entry-chevron">›</span>
                                </div>
                                <div className="gip-entry-row" onClick={media.openMsgSearch}>
                                    <span className="gip-entry-icon"><Search size={17} /></span>
                                    <span className="gip-entry-label">{t("chat.group.searchMessages")}</span>
                                    <span className="gip-entry-chevron">›</span>
                                </div>
                                <div className="gip-entry-row" onClick={() => { setShowFavorites(true); loadFavorites(null); }}>
                                    <span className="gip-entry-icon"><Star size={17} /></span>
                                    <span className="gip-entry-label">{t("chat.favorites")}</span>
                                    <span className="gip-entry-chevron">›</span>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="gip-section">
                                <div className="cmu-stats-grid">
                                    <div className="cmu-stat">
                                        <span className="cmu-stat-value">{chatSelected.total_posts ?? 0}</span>
                                        <span className="cmu-stat-label">publicações</span>
                                    </div>
                                    <div className="cmu-stat">
                                        <span className="cmu-stat-value">{chatSelected.total_followers ?? 0}</span>
                                        <span className="cmu-stat-label">seguidor</span>
                                    </div>
                                    <div className="cmu-stat">
                                        <span className="cmu-stat-value">{chatSelected.total_following ?? 0}</span>
                                        <span className="cmu-stat-label">seguindo</span>
                                    </div>
                                    <div className="cmu-stat">
                                        <span className="cmu-stat-value">{chatSelected.total_comments ?? 0}</span>
                                        <span className="cmu-stat-label">comentários</span>
                                    </div>
                                </div>
                            </div>

                            {/* View profile */}
                            <div className="gip-leave-wrap" style={{ marginTop: 8 }}>
                                <button
                                    className="foc-btn foc-btn-primary"
                                    onClick={() => { onClose(); navigate(`/@${chatSelected.user_name}`); }}
                                >
                                    Ver perfil
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatMenu;
